/**
 * Monaco JSON Editor Component
 *
 * A wrapper around Monaco Editor configured for JSON editing with:
 * - Syntax highlighting
 * - Real-time validation
 * - Auto-formatting
 * - Error detection
 */

import { Editor } from '@monaco-editor/react';
import { Alert } from 'react-bootstrap';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MonacoJsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: string | number;
  readOnly?: boolean;
  placeholder?: string;
}

export default function MonacoJsonEditor({
  value,
  onChange,
  height = 400,
  readOnly = false,
  placeholder = '{\n  "Version": "2025-01-01",\n  "Statement": []\n}',
}: MonacoJsonEditorProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const handleEditorChange = (newValue: string | undefined) => {
    const val = newValue || '';
    onChange(val);

    // Validate JSON
    if (val.trim()) {
      try {
        JSON.parse(val);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('admin.policyDatasets.invalidJson'));
      }
    } else {
      setError(null);
    }
  };

  const handleEditorMount = (editor: any, monaco: any) => {
    // Configure JSON validation
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      schemas: [],
      enableSchemaRequest: false,
    });

    // Set editor options
    editor.updateOptions({
      minimap: { enabled: false },
      lineNumbers: 'on',
      roundedSelection: false,
      scrollBeyondLastLine: false,
      readOnly: readOnly,
      automaticLayout: true,
      tabSize: 2,
      formatOnPaste: true,
      formatOnType: true,
    });
  };

  return (
    <div>
      {error && (
        <Alert variant="danger" className="mb-2">
          <small>
            <strong>{t('admin.policyDatasets.jsonError')}:</strong> {error}
          </small>
        </Alert>
      )}
      <div className="border rounded" style={{ overflow: 'hidden' }}>
        <Editor
          height={height}
          defaultLanguage="json"
          value={value || placeholder}
          onChange={handleEditorChange}
          onMount={handleEditorMount}
          theme="vs-light"
          options={{
            minimap: { enabled: false },
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            readOnly: readOnly,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
          }}
        />
      </div>
      {!error && value.trim() && (
        <small className="text-success mt-1 d-block">
          <i className="fas fa-check-circle me-1"></i>
          {t('admin.policyDatasets.validJson')}
        </small>
      )}
    </div>
  );
}
